import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'

import type { MediaAccept } from '@ems/cms/fields'

import {
  fetchCollections,
  fetchDocument,
  fetchDocuments,
  fetchEditionLocales,
  fetchSite,
  fetchMediaByKey,
  fetchMediaLibrary,
  fetchMediaSource,
  fetchVersions,
} from '#admin/features/cms/api.ts'

/**
 * The console's CMS queries.
 *
 * `collections` is the one worth a note: it is the field list every form renders from, it comes
 * from code rather than from a table, and it changes only when the API is deployed. So it is
 * cached for the session rather than refetched per screen — `staleTime: Infinity` is accurate
 * here in a way it almost never is.
 */
export const collectionsQuery = () =>
  queryOptions({
    queryKey: ['cms', 'collections'],
    queryFn: ({ signal }) => fetchCollections(signal),
    staleTime: Number.POSITIVE_INFINITY,
  })

export const documentsQuery = (filter: { collection?: string; status?: string } = {}) =>
  queryOptions({
    // The filter is part of the key, so switching collections is a cache hit on the way back
    // rather than a refetch — an editor moving between News and Pages does that constantly.
    queryKey: ['cms', 'documents', filter],
    queryFn: ({ signal }) => fetchDocuments(filter, signal),
  })

export const editionLocalesQuery = () =>
  queryOptions({
    queryKey: ['cms', 'locales'],
    queryFn: ({ signal }) => fetchEditionLocales(signal),
  })

/** The site's address. It changes only when a domain changes, so it is read once a session. */
export const siteQuery = () =>
  queryOptions({
    queryKey: ['cms', 'site'],
    queryFn: ({ signal }) => fetchSite(signal),
    staleTime: Number.POSITIVE_INFINITY,
  })

export const documentQuery = (id: string) =>
  queryOptions({
    queryKey: ['cms', 'document', id],
    queryFn: ({ signal }) => fetchDocument(id, signal),
  })

/**
 * A preview URL for one media key.
 *
 * The URL the API signs lives five minutes, so it is fresh for four: long enough that the editor
 * does not refetch per render, short enough that a remount never hands `<img>` an expired link.
 * No retry, because the usual failure is a 404 for an unregistered key, which retrying cannot fix.
 */
export const mediaSourceQuery = (objectKey: string) =>
  queryOptions({
    queryKey: ['cms', 'media-source', objectKey],
    queryFn: ({ signal }) => fetchMediaSource(objectKey, signal),
    staleTime: 4 * 60 * 1000,
    retry: false,
  })

/**
 * The picker's pages. Fresh for four minutes for the same reason as `mediaSourceQuery`: each item
 * carries a five-minute URL, and a grid reopened after that would draw broken thumbnails.
 */
export const mediaLibraryQuery = (q: string, accept: MediaAccept) =>
  infiniteQueryOptions({
    queryKey: ['cms', 'media-library', accept, q],
    queryFn: ({ pageParam, signal }) => fetchMediaLibrary({ q, accept, cursor: pageParam }, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 4 * 60 * 1000,
  })

/** The library row behind one key, for the focal-point editor. Same freshness as the picker. */
export const mediaByKeyQuery = (objectKey: string) =>
  queryOptions({
    queryKey: ['cms', 'media-by-key', objectKey],
    queryFn: ({ signal }) => fetchMediaByKey(objectKey, signal),
    staleTime: 4 * 60 * 1000,
  })

export const versionsQuery = (id: string) =>
  queryOptions({
    queryKey: ['cms', 'versions', id],
    queryFn: ({ signal }) => fetchVersions(id, signal),
  })
